import * as React from 'react';

import {SegmentedControl} from 'shells/card/segmented-control';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {PreviewableSection} from 'shells/layout/page-content-previewable/previewable-section';
import {Stack} from 'shells/layout/stack/stack';
import type {SaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {Body} from 'shells/typography/body';
import {LabelWithContent} from 'shells/typography/label-with-content';

import type {ChatAssignmentStrategyEnum} from 'nutshell-graphql-types';
import type {
    GetBehaviorSettings_chatSettings as BehaviorSettings,
    ChatSettingsInput,
    ChatSettingsOfflineFormTypeEnum as OfflineFormType,
} from 'nutshell-graphql-types';

import {
    SAVE_STATE_KEYS,
    OFFLINE_MESSAGE_LIMIT,
    FORM_TYPE_CONVERSATIONAL,
    FORM_TYPE_NONE,
} from '../consts';
import type {SaveStateKey} from '../consts';
import {AssignmentOptionsContentNew as AssignmentOptionsContent} from '../goals/human-options-section/assignment-options-content-new';
import {BUTTON_OPTIONS} from '../goals/human-options-section/consts';
import {OfflineEnabledContent} from '../offline-options/offline-enabled-content';

import {OFFLINE_RULES_SECTION_ID} from './chat-behavior-options';

type Props = {
    chatSettings: BehaviorSettings;
    savingToastKey: SaveStateKey | null | undefined;
    toastState: SaveState;
    handleSave: (input: ChatSettingsInput, saveStateKey: SaveStateKey) => void;
    formType: OfflineFormType;
    setFormType: (type: OfflineFormType) => void;
    message: string;
    setMessage: (message: string) => void;
    setPreviewedSectionId: (sectionId?: string | null | undefined) => void;
};

export function HandoffRulesSection(props: Props) {
    const [assignmentStrategy, setAssignmentStrategy] = React.useState<ChatAssignmentStrategyEnum>(
        props.chatSettings.assignment.strategy
    );
    const [assignmentSpecificUserId, setAssignmentSpecificUserId] = React.useState<
        string | null | undefined
    >(
        props.chatSettings.assignment.strategy === 'SPECIFIC_USER' &&
            props.chatSettings.assignment.user
            ? props.chatSettings.assignment.user.id
            : null
    );

    const isConversational = props.formType === FORM_TYPE_CONVERSATIONAL;
    const isOfflineFormEnabled = props.formType !== FORM_TYPE_NONE;

    return (
        <Stack spacing={16}>
            <Body size='large' isBold={true}>
                Handoff rules
            </Body>
            <Stack spacing={8}>
                <Body isBold={true}>Online rules</Body>
                <AssignmentOptionsContent
                    assignedUserId={assignmentSpecificUserId}
                    selectedValue={assignmentStrategy}
                    setSelectedValue={(strategy) => {
                        setAssignmentStrategy(strategy);
                        props.handleSave(
                            {
                                assignment: {
                                    assignmentStrategy: strategy,
                                    assignmentSpecificUserId,
                                },
                            },
                            SAVE_STATE_KEYS.ASSIGNMENT
                        );
                    }}
                    setAssignedUserId={(userId) => {
                        setAssignmentSpecificUserId(userId);
                        props.handleSave(
                            {
                                assignment: {
                                    assignmentStrategy,
                                    assignmentSpecificUserId: userId,
                                },
                            },
                            SAVE_STATE_KEYS.ASSIGNMENT
                        );
                    }}
                    savingToastKey={props.savingToastKey}
                    toastState={props.toastState}
                />
            </Stack>
            <PreviewableSection
                onFocus={props.setPreviewedSectionId}
                onBlur={() => {
                    if (props.setPreviewedSectionId) {
                        props.setPreviewedSectionId(null);
                    }
                }}
                sectionId={OFFLINE_RULES_SECTION_ID}
            >
                <Stack spacing={8}>
                    <Body isBold={true}>Offline rules</Body>
                    <LabelWithContent
                        label={<Body>When human agents are all offline:</Body>}
                        state={
                            props.savingToastKey === SAVE_STATE_KEYS.OFFLINE_TYPE
                                ? props.toastState
                                : 'initial'
                        }
                        spacing={8}
                        content={
                            <SegmentedControl
                                buttonOptions={BUTTON_OPTIONS}
                                selectedValue={props.formType}
                                setSelectedValue={(type) => {
                                    props.setFormType(type);
                                    props.handleSave(
                                        {
                                            offline: {
                                                message:
                                                    type === FORM_TYPE_CONVERSATIONAL
                                                        ? undefined
                                                        : props.message,
                                                conversationalMessage:
                                                    type === FORM_TYPE_CONVERSATIONAL
                                                        ? props.message
                                                        : undefined,
                                                formSlug:
                                                    props.chatSettings.offline.form.publicFormId,
                                                isEnabled: type !== FORM_TYPE_NONE,
                                                formType: type,
                                            },
                                        },
                                        SAVE_STATE_KEYS.OFFLINE_TYPE
                                    );
                                }}
                                isAnimated={true}
                            />
                        }
                    />
                    {!isOfflineFormEnabled && (
                        <FlashBanner type='info' hasBorder={true}>
                            When hiding Nutshell chat, users can still interact with you if other
                            engagement bar actions are visible.
                        </FlashBanner>
                    )}
                    {isOfflineFormEnabled && (
                        <OfflineEnabledContent
                            message={props.message}
                            setMessage={(message) => {
                                props.setMessage(message);
                                if (message.length > 0 && message.length <= OFFLINE_MESSAGE_LIMIT) {
                                    props.handleSave(
                                        {
                                            offline: {
                                                message: isConversational ? undefined : message,
                                                conversationalMessage: isConversational
                                                    ? message
                                                    : undefined,
                                                isEnabled: isOfflineFormEnabled,
                                                formType: props.formType,
                                            },
                                        },
                                        SAVE_STATE_KEYS.OFFLINE_MESSAGE
                                    );
                                }
                            }}
                            form={props.chatSettings.offline.form}
                            savingToastKey={props.savingToastKey}
                            toastState={props.toastState}
                            // @ts-expect-error
                            setPreviewedSectionId={props.setPreviewedSectionId}
                        />
                    )}
                </Stack>
            </PreviewableSection>
        </Stack>
    );
}
